/**
 * Where a rope actually touches a wheel: the TANGENT point.
 *
 * A rope does not meet a pulley at its centre and it does not meet it at a
 * fixed point on the rim. It meets it where the straight run runs out — the
 * tangent — and where that is depends on where the other end of the run is.
 * So a contact point is a function of the whole arrangement, not a property of
 * the part, which is why this is a solve and not a port.
 *
 * ## The one equation
 *
 * A run has a direction `û`. Along it, each wheel's centre sits to one side by
 * exactly its radius, because the radius to a tangent point is perpendicular
 * to the tangent. Writing `p̂` for `û` turned a quarter turn, a stop's contact
 * point is
 *
 *     contact = centre + σ·r·p̂          σ = ±1, which side the rope lies
 *
 * and the run must join the two of them, so `û` is parallel to
 *
 *     (c_b − c_a) + (σ_b·r_b − σ_a·r_a)·p̂
 *
 * Taking the cross product with `û` kills the first term's parallel part and
 * leaves, with `D = c_b − c_a` and `k = σ_b·r_b − σ_a·r_a`,
 *
 *     D × û + k = 0     →     |D|·sin(θ − φ) = −k
 *
 * which is one `asin`. No iteration, and it covers all three cases — point to
 * wheel, wheel to point, wheel to wheel — because a point is a wheel of radius
 * zero.
 *
 * ## When there is no answer
 *
 * `|k| > |D|`: the run is asked to be tangent to two circles it cannot reach
 * past — a part inside a wheel, or two wheels overlapping. There is no such
 * rope, and saying so is better than returning a line that crosses the middle
 * of a pulley.
 */
import { Vec2 } from './types';

/** One end of a run, as this solver sees it. */
export interface Stop {
    /** The part's centre. */
    at: Vec2;
    /** Zero for anything that is not a wheel. */
    radius: number;
    /**
     * Which side of the run the centre lies on, walking the rope in its path
     * order. `+1` and `−1`; meaningless at radius zero.
     *
     * The same value for BOTH runs at a wheel the rope passes over, and that
     * is the whole of what "it wraps" means: walk the rope over a pulley and
     * the axle stays on the same hand the entire way round.
     */
    side: number;
}

export interface Taut {
    pa: Vec2;
    pb: Vec2;
    /** Unit vector along the run, from `pa` to `pb`. */
    dir: Vec2;
    /** The straight run only. The arc is added by whoever owns the wrap. */
    length: number;
}

/** The straight run between two stops, tangent to each. `null` when the two
 *  are too close together for such a run to exist. */
export function taut(a: Stop, b: Stop): Taut | null {
    const D = { x: b.at.x - a.at.x, y: b.at.y - a.at.y };
    const d = Math.hypot(D.x, D.y);
    const k = b.side * b.radius - a.side * a.radius;
    if (d < 1e-12 || Math.abs(k) > d) return null;

    const theta = Math.atan2(D.y, D.x) + Math.asin(-k / d);
    const dir = { x: Math.cos(theta), y: Math.sin(theta) };
    // `dir` turned a quarter turn: the direction a centre is offset from its
    // contact point, for a stop whose `side` is +1.
    const p = { x: dir.y, y: -dir.x };

    const pa = { x: a.at.x + a.side * a.radius * p.x, y: a.at.y + a.side * a.radius * p.y };
    const pb = { x: b.at.x + b.side * b.radius * p.x, y: b.at.y + b.side * b.radius * p.y };
    return { pa, pb, dir, length: Math.hypot(pb.x - pa.x, pb.y - pa.y) };
}
